import React, { useContext, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Divider,
  IconButton,
  Typography,
  Box,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Assistant as AssistantIcon,
  Map as MapIcon,
  List as ListIcon,
  Build as BuildIcon,
  Category as CategoryIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  AccountCircle as AccountCircleIcon,
  ExitToApp as ExitToAppIcon,
  Apps as AppsIcon,
  NotificationsActive as NotificationsActiveIcon,
  AutoMode as AutoModeIcon,
} from '@mui/icons-material';
import { GlobalContext } from '../GlobalProvider';
import tnIcon from '../assets/tn.png';

const Sidebar = () => {
  const { loginDetails, orgDetails, currentApp } = useContext(GlobalContext);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isUserDrawerOpen, setIsUserDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const toggleUserDrawer = (open) => setIsUserDrawerOpen(open);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleSidebarToggle = () => setIsMinimized(!isMinimized);

  // If not authenticated, don't render the sidebar
  if (!loginDetails) return null;

  const menuItems = !currentApp.adminApp
    ? [
      { path: '/', icon: <DashboardIcon />, label: 'Dashboard' },
      { path: '/birdview', icon: <MapIcon />, label: 'Bird View' },
      { path: '/overturemaps', icon: <MapIcon />, label: 'Overture Maps' },
      { path: '/map-editor', icon: <MapIcon />, label: 'Map Editor' },
      { path: '/entity-incidents', icon: <BuildIcon />, label: 'Incidents' },
      { path: '/entities', icon: <CategoryIcon />, label: 'Entities' },
      { path: '/triggers', icon: <NotificationsActiveIcon />, label: 'Triggers' },
      { path: '/triggeractions', icon: <AutoModeIcon />, label: 'Trigger Actions' },
      { path: '/audit', icon: <ListIcon />, label: 'Audit Actions' },
   //   { path: '/agents', icon: <AssistantIcon />, label: 'AI Agents' },
    ]
    : [
      { path: '/users', icon: <PeopleIcon />, label: 'Users' },
      { path: '/organizations', icon: <BusinessIcon />, label: 'Organizations' },
      { path: '/roles', icon: <SecurityIcon />, label: 'Roles' },
   //   { path: '/agents', icon: <AssistantIcon />, label: 'AI Agents' },
    ];

  return (
    <>
      {/* Main Sidebar */}
      <Drawer
        variant="permanent"
        anchor="left"
        PaperProps={{ style: { width: isMinimized ? '70px' : '240px', transition: 'width 0.3s' } }}
      >
        <Toolbar sx={{
          paddingLeft: '10px !important', // Ensure it overrides any other styles
          paddingRight: '0px', // Adjust right padding if needed
        }} disableGutters>
          <IconButton onClick={handleSidebarToggle}>
            <MenuIcon />
          </IconButton>
          {!isMinimized && (
            <>
              <img
                src={tnIcon}
                alt="Truenorth"
                style={{ width: '50px', height: '50px', marginRight: '10px' }}
              />
              <Typography
                variant="h5"
                noWrap
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase'
                }}
              >
                Apollo
              </Typography>
            </>
          )}
        </Toolbar>
        <List>
          {menuItems.map(({ path, icon, label }) => (
            <Tooltip key={path} title={isMinimized ? label : ''} placement="right">
              <ListItem
                button
                component={Link}
                to={path}
                selected={location.pathname === path}
              >
                <ListItemIcon>{icon}</ListItemIcon>
                {!isMinimized && <ListItemText primary={label} />}
              </ListItem>
            </Tooltip>
          ))}
       


        </List>
        <Divider />
        {/* Bottom Menu */}

        <List style={{ marginTop: 'auto' }}>



          <Tooltip title={isMinimized ? 'Apps' : ''} placement="right">
            <ListItem button component={Link} to="/apps">
              <ListItemIcon>
                <AppsIcon />
              </ListItemIcon>
              {!isMinimized && <ListItemText primary="Apps" />}
            </ListItem>
          </Tooltip>
          <Tooltip title={isMinimized ? `${loginDetails.firstName}` : ''} placement="right">
            <ListItem button onClick={() => toggleUserDrawer(true)}>
              <ListItemIcon>
                <AccountCircleIcon />
              </ListItemIcon>
              {!isMinimized && (
                <ListItemText
                  primary={`${loginDetails.firstName} ${loginDetails.lastName}`}
                />
              )}
            </ListItem>
          </Tooltip>
          {/* {currentApp && <Tooltip title={isMinimized ? 'Apps' : ''} placement="right">
            <ListItem button component={Link} to="/apps">

              {!isMinimized && <ListItemText primary={<strong>{currentApp.name}</strong>} />}
            </ListItem>
          </Tooltip>
          } */}
        </List>
      </Drawer>

      {/* User Drawer */}
      <Drawer
        anchor="right"
        open={isUserDrawerOpen}
        onClose={() => toggleUserDrawer(false)}
        PaperProps={{
          style: { width: '400px', padding: '20px', backgroundColor: '#f4f4f4' },
        }}
      >
        <Toolbar>
          <Typography variant="h6" style={{ flexGrow: 1 }}>
            User Info
          </Typography>
          <IconButton
            edge="end"
            onClick={() => toggleUserDrawer(false)}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
        <Divider />
        <Box p={3}>
          <Typography variant="h6" gutterBottom>
            Welcome, {loginDetails.firstName} {loginDetails.lastName}
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Email:</strong> {loginDetails.email}
          </Typography>
          {orgDetails && (
            <Typography variant="body1" gutterBottom>
              <strong>Organization:</strong> {orgDetails.name}
            </Typography>
          )}
          {currentApp && (
            <Typography variant="body1" gutterBottom>
              <strong>Current App:</strong> {currentApp.name}
            </Typography>
          )}
          <Divider style={{ margin: '20px 0' }} />
          <List>
            <ListItem button onClick={handleLogout}>
              <ListItemIcon>
                <ExitToAppIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default Sidebar;
